import {
  chartsTooltipClasses,
  ChartsTooltipContainer,
  type ChartsTooltipProps,
  useAxesTooltip,
} from '@mui/x-charts/ChartsTooltip';

type BarTooltipBreakdownRow = { label: string; value: number; color?: string };
export type BarTooltipBreakdown = { total?: number; rows: BarTooltipBreakdownRow[] };
export type BarTooltipBreakdownByLabel = Readonly<Record<string, BarTooltipBreakdown | undefined>>;

type BreakdownTooltipProps = ChartsTooltipProps & {
  breakdownByLabel: BarTooltipBreakdownByLabel;
};

type BreakdownTooltipTableProps = {
  axisFormattedValue: string;
  breakdown: BarTooltipBreakdown | undefined;
  labelCellClassName: string;
  rowClassName: string;
  seriesItems: Array<{
    color?: string;
    formattedLabel?: string | null;
    value: unknown;
  }>;
  tableClassName: string;
  valueCellClassName: string;
};

const BreakdownTooltipTable = ({
  axisFormattedValue,
  breakdown,
  labelCellClassName,
  rowClassName,
  seriesItems,
  tableClassName,
  valueCellClassName,
}: BreakdownTooltipTableProps): JSX.Element => {
  const fallbackTotal = seriesItems.reduce((sum, item) => {
    const next = typeof item.value === 'number' ? item.value : Number(item.value);
    return Number.isFinite(next) ? sum + next : sum;
  }, 0);
  const total = breakdown?.total ?? fallbackTotal;
  const rows =
    breakdown?.rows ??
    seriesItems.map((item) => ({
      label: item.formattedLabel ?? '',
      value: Number(item.value),
      color: item.color,
    }));

  return (
    <table className={tableClassName}>
      <thead>
        <tr>
          <th className={labelCellClassName}>{axisFormattedValue}</th>
          <td className={valueCellClassName}>{`Total: ${total}`}</td>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={`${axisFormattedValue}-${row.label}-${index}`} className={rowClassName}>
            <th className={labelCellClassName}>{row.label}</th>
            <td className={valueCellClassName}>{row.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const BreakdownTooltip = ({
  breakdownByLabel,
  classes,
  sx,
  ...props
}: BreakdownTooltipProps): JSX.Element | null => {
  const tooltipData = useAxesTooltip();
  if (!tooltipData) return null;
  const paperClassName = `${classes?.paper ?? ''} flowqa-bar-tooltip-paper`.trim();
  const tableClassName =
    `${chartsTooltipClasses.table} ${classes?.table ?? ''} flowqa-bar-tooltip-table`.trim();
  const rowClassName = chartsTooltipClasses.row;
  const cellClassName =
    `${chartsTooltipClasses.cell} ${classes?.cell ?? ''} flowqa-bar-tooltip-cell`.trim();
  const labelCellClassName = `${cellClassName} ${chartsTooltipClasses.labelCell}`.trim();
  const valueCellClassName = `${cellClassName} ${chartsTooltipClasses.valueCell}`.trim();

  return (
    <ChartsTooltipContainer classes={classes} sx={sx} {...props}>
      <div className={paperClassName}>
        {tooltipData.map(({ axisId, axisFormattedValue, seriesItems }) => {
          const breakdown = breakdownByLabel[axisFormattedValue];
          return (
            <BreakdownTooltipTable
              key={String(axisId)}
              axisFormattedValue={axisFormattedValue}
              breakdown={breakdown}
              labelCellClassName={labelCellClassName}
              rowClassName={rowClassName}
              seriesItems={seriesItems}
              tableClassName={tableClassName}
              valueCellClassName={valueCellClassName}
            />
          );
        })}
      </div>
    </ChartsTooltipContainer>
  );
};
